import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import './SubscriptionPlanCards.less';
import { IEquipmentEntry } from 'types/IEquipmentEntry';
import {
  BillingPeriod,
  PlanPricing,
  PremiumPlanInfoTooltip,
  useBasicBenefitsList,
  usePremiumBenefitsList,
} from 'app/components/subscriptions/dialogs/ManageYourPlanDialog';
import {
  BillingSwitch,
  PremiumInfoPlanCard,
  SubscriptionPlanComparisonGrid,
} from './components';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import { useFullscreenStripeLoader } from 'context';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { formatDate } from 'utils/date';
import { SUBSCRIPTION_TYPE } from 'types/IEquipmentEntry';
import { ConfirmationDialog } from 'app/components/dialogs/ConfirmationDialog';
import { subscriptionsApi } from 'services/api/subscription/queries';
import { useRequireUserNames } from 'app/components/dialogs/SetUserNamesDialog';
interface Props {
  equipment: IEquipmentEntry;
  hideBasicCard?: boolean;
}

const PRICE_INFO_KEY_NAME = 'PREMIUM_TELEMATICS';

export default function SubscriptionPlanCards({
  equipment,
  hideBasicCard,
}: Props) {
  const { t } = useTranslation();
  const { requireUserNames } = useRequireUserNames();

  const isMounted = useRef(true);

  const { addErrorModal } = useContext(GlobalErrorContext);

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const isAnnuallyBilling = billingPeriod === 'annually';

  const getPricingInformationQuery =
    subscriptionsApi.useGetPricingInformationQuery({
      keyName: PRICE_INFO_KEY_NAME,
      businessUnit: equipment.telematics?.businessUnit!,
    });

  const premiumBenefits = usePremiumBenefitsList();
  const basicBenefits = useBasicBenefitsList();

  const [createCheckoutSession, { isLoading: isCreatingCheckoutSession }] =
    subscriptionsApi.useCreateCheckoutSessionMutation();

  const { show: showStripeLoader, hide: hideStripeLoader } =
    useFullscreenStripeLoader();

  const [isUpgradeSoonDialogOpen, setIsUpgradeSoonDialogOpen] = useState(false);
  const [upgradeAvailableAfterDate, setUpgradeAvailableAfterDate] =
    useState<string>('');

  const planPricing = useMemo(() => {
    return getPricingInformationQuery?.data?.reduce<PlanPricing>(
      (acc, info) => {
        const interval = info.recurring?.interval;
        const amount = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: info.unit_amount % 100 === 0 ? 0 : 2,
          maximumFractionDigits: 2,
        }).format(info.unit_amount / 100);

        if (interval === 'year') {
          return { ...acc, annually: { id: info.id, amount } };
        }

        if (interval === 'month') {
          return { ...acc, monthly: { id: info.id, amount } };
        }

        return acc;
      },
      {
        annually: { id: 'initial_annually_id', amount: '' },
        monthly: {
          id: 'initial_monthly_id',
          amount: '',
        },
      },
    );
  }, [getPricingInformationQuery.data]);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      hideStripeLoader();
    };
  }, []);

  useEffect(() => {
    if (
      getPricingInformationQuery.isError &&
      getPricingInformationQuery.error
    ) {
      addErrorModal();
    }
  }, [getPricingInformationQuery.isError, getPricingInformationQuery.error]);

  async function handleUpgrade() {
    if (!requireUserNames()) {
      return;
    }

    if (!planPricing) return;

    const initialSubscription = equipment.subscriptions?.find(
      sub => sub.type === SUBSCRIPTION_TYPE.INITIAL_TELEMATICS,
    );

    if (initialSubscription?.endDate) {
      const today = moment().startOf('day');
      const end = moment(initialSubscription.endDate).startOf('day');
      const daysUntilEnd = end.diff(today, 'days');

      if (daysUntilEnd > 730) {
        const availableAfter = end.clone().subtract(730, 'days');
        setUpgradeAvailableAfterDate(
          formatDate(availableAfter.toISOString(), 'DATE_FORMAT_READABLE'),
        );
        setIsUpgradeSoonDialogOpen(true);
        return;
      }
    }

    const price = isAnnuallyBilling
      ? planPricing.annually.id
      : planPricing.monthly.id;
    const equipmentUrl = `${window.location.origin}/#/equipment/${equipment.id}`;

    showStripeLoader();

    try {
      const { checkoutUrl } = await createCheckoutSession({
        price,
        equipmentId: equipment.id,
        successUrl: `${equipmentUrl}/subscriptionSuccess`,
        cancelUrl: equipmentUrl,
      }).unwrap();

      if (!isMounted.current) return;

      hideStripeLoader();
      window.location.href = checkoutUrl;
    } catch (error) {
      if (!isMounted.current) return;
      hideStripeLoader();
      addErrorModal({
        title: t(
          'subscriptions.manage_your_plan.errors.account_conflict.title',
        ),
        description: t(
          'subscriptions.manage_your_plan.errors.account_conflict.description',
        ),
      });
    }
  }

  if (hideBasicCard) {
    return (
      <>
        <div className="subscription-plan-cards subscription-plan-cards__premium-only">
          <BillingSwitch
            billingPeriod={billingPeriod}
            planPricing={planPricing}
            isLoading={getPricingInformationQuery.isLoading}
            setBillingPeriod={setBillingPeriod}
          />

          <PremiumInfoPlanCard
            isAnnuallyBilling={isAnnuallyBilling}
            planPricing={planPricing}
            isLoading={getPricingInformationQuery.isLoading}
            isUpgrading={isCreatingCheckoutSession}
            onUpgrade={handleUpgrade}
          />
        </div>

        <ConfirmationDialog
          open={isUpgradeSoonDialogOpen}
          title={t(
            'subscriptions.manage_your_plan.upgrade_available_soon.title',
          )}
          confirmationLabel={t('ok')}
          onConfirm={() => setIsUpgradeSoonDialogOpen(false)}
        >
          <p>
            {t('subscriptions.manage_your_plan.upgrade_available_soon.body', {
              date: upgradeAvailableAfterDate,
            })}
          </p>
        </ConfirmationDialog>
      </>
    );
  }

  return (
    <>
      <div className="subscription-plan-cards__switch-container">
        <BillingSwitch
          billingPeriod={billingPeriod}
          planPricing={planPricing}
          isLoading={getPricingInformationQuery.isLoading}
          setBillingPeriod={setBillingPeriod}
        />
      </div>

      <SubscriptionPlanComparisonGrid
        card1={{
          title: (
            <>
              {t(
                'subscriptions.manage_your_plan.premium_telematics_plan.title',
              )}

              <PremiumPlanInfoTooltip />
            </>
          ),
          subtitle: (
            <span className="premium-info-plan-card__subtitle">
              <span className="premium-info-plan-card__price">
                $
                {planPricing
                  ? isAnnuallyBilling
                    ? planPricing.annually.amount
                    : planPricing.monthly.amount
                  : 0}
              </span>
              {t(
                `subscriptions.manage_your_plan.premium_telematics_plan.${
                  isAnnuallyBilling ? 'price_per_year' : 'price_per_month'
                }`,
              )}
            </span>
          ),
          isSubtitleLoading: getPricingInformationQuery.isLoading,
          buttonLabel: t(`subscriptions.manage_your_plan.upgrade`),
          isButtonLoading:
            getPricingInformationQuery.isLoading || isCreatingCheckoutSession,
          featuresTitle: {
            i18nKey:
              'subscriptions.manage_your_plan.premium_telematics_plan.access_to_all',
          },
          features: premiumBenefits,
          onUpgrade: handleUpgrade,
        }}
        card2={{
          isCurrent: true,
          title: t('subscriptions.manage_your_plan.basic_plan_view.title'),
          subtitle: (
            <span className="basic-info-plan-card__subtitle">
              {t('subscriptions.manage_your_plan.basic_plan_view.free')}
            </span>
          ),
          isSubtitleLoading: getPricingInformationQuery.isLoading,
          buttonLabel: t(
            'subscriptions.manage_your_plan.basic_plan_view.current_plan',
          ),
          isButtonLoading:
            getPricingInformationQuery.isLoading || isCreatingCheckoutSession,
          featuresTitle: t(
            'subscriptions.manage_your_plan.basic_plan_view.basic_plan_features',
          ),
          features: basicBenefits,
        }}
      />

      <ConfirmationDialog
        open={isUpgradeSoonDialogOpen}
        title={t('subscriptions.manage_your_plan.upgrade_available_soon.title')}
        confirmationLabel={t('ok')}
        onConfirm={() => setIsUpgradeSoonDialogOpen(false)}
      >
        <p>
          {t('subscriptions.manage_your_plan.upgrade_available_soon.body', {
            date: upgradeAvailableAfterDate,
          })}
        </p>
      </ConfirmationDialog>
    </>
  );
}
